import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getHealth, vouchersApi, routersApi } from "../api/client.ts";
import { useAuth } from "../lib/auth.tsx";
import type { VoucherRow } from "../lib/types.ts";

export function DashboardPage() {
  const { tenant } = useAuth();
  const auth = { tenantId: tenant!.tenantId, idToken: tenant!.idToken };

  const health = useQuery({
    queryKey: ["health"],
    queryFn: () => getHealth(),
    refetchInterval: 30_000,
  });

  const vouchers = useQuery({
    queryKey: ["vouchers", auth.tenantId, "dashboard"],
    queryFn: () => vouchersApi.list(auth, { limit: 200 }),
    refetchInterval: 30_000,
  });

  const routers = useQuery({
    queryKey: ["routers", auth.tenantId],
    queryFn: () => routersApi.list(auth),
    refetchInterval: 60_000,
  });

  const stats = useMemo(() => {
    const vs = vouchers.data ?? [];
    const rs = routers.data ?? [];
    const now = Date.now();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();

    const isToday = (iso: string) => new Date(iso).getTime() >= todayMs;
    const isPaid = (s: VoucherRow["status"]) => s === "paid" || s === "active";

    const paidToday = vs.filter((v) => isPaid(v.status) && isToday(v.createdAt));
    const revenueToday = paidToday.reduce(
      (sum, v) => sum + v.priceKesSnapshot,
      0,
    );

    const active = vs.filter(
      (v) =>
        v.status === "active" &&
        v.expiresAt &&
        new Date(v.expiresAt).getTime() > now,
    ).length;

    return {
      revenueToday,
      paidTodayCount: paidToday.length,
      active,
      routerCount: rs.length,
      onlineRouters: rs.filter((r) => r.status === "online").length,
      pendingVouchers: vs.filter((v) => v.status === "pending").length,
    };
  }, [vouchers.data, routers.data]);

  const recent = (vouchers.data ?? []).slice(0, 8);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Dashboard</h2>
          <p className="text-sm text-slate-500">
            Snapshot of today's hotspot activity.
          </p>
        </div>
        <ApiStatus
          loading={health.isLoading}
          ok={health.data?.ok === true}
          error={!!health.error}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Revenue today"
          value={`KES ${stats.revenueToday.toLocaleString()}`}
          hint={`${stats.paidTodayCount} paid voucher${stats.paidTodayCount === 1 ? "" : "s"}`}
        />
        <Stat
          label="Active vouchers"
          value={String(stats.active)}
          hint="Currently online sessions"
        />
        <Stat
          label="Routers"
          value={`${stats.onlineRouters} / ${stats.routerCount}`}
          hint="Online / total"
        />
        <Stat
          label="Pending payments"
          value={String(stats.pendingVouchers)}
          hint="STK Push awaiting customer"
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">Recent vouchers</h3>
          <a
            href="/vouchers"
            className="text-xs font-medium text-brand-700 hover:underline"
          >
            View all →
          </a>
        </div>
        {vouchers.isLoading ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500">
            Loading…
          </p>
        ) : recent.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500">
            No vouchers yet. Once a customer completes STK Push, it shows up here.
          </p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-2 font-medium">Time</th>
                <th className="px-5 py-2 font-medium">Phone</th>
                <th className="px-5 py-2 font-medium">Package</th>
                <th className="px-5 py-2 font-medium">Amount</th>
                <th className="px-5 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {recent.map((v) => (
                <tr key={v.id}>
                  <td className="px-5 py-2 text-xs text-slate-500">
                    {new Date(v.createdAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-2 font-mono text-xs text-slate-600">
                    {v.phone}
                  </td>
                  <td className="px-5 py-2 text-slate-700">
                    {v.packageNameSnapshot}
                  </td>
                  <td className="px-5 py-2 text-slate-600">
                    {v.priceKesSnapshot.toLocaleString()}
                  </td>
                  <td className="px-5 py-2">
                    <StatusPill status={v.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight">{value}</p>
      <p className="mt-1 text-xs text-slate-400">{hint}</p>
    </div>
  );
}

function StatusPill({ status }: { status: VoucherRow["status"] }) {
  const tone: Record<VoucherRow["status"], string> = {
    pending: "bg-amber-50 text-amber-700",
    paid: "bg-brand-50 text-brand-700",
    active: "bg-blue-50 text-blue-700",
    expired: "bg-slate-100 text-slate-500",
    refunded: "bg-slate-100 text-slate-500",
    failed: "bg-red-50 text-red-700",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone[status]}`}>
      {status}
    </span>
  );
}

function ApiStatus({
  loading,
  ok,
  error,
}: {
  loading: boolean;
  ok: boolean;
  error: boolean;
}) {
  const tone = error
    ? "bg-red-50 text-red-700 border-red-200"
    : ok
      ? "bg-brand-50 text-brand-700 border-brand-200"
      : "bg-slate-50 text-slate-600 border-slate-200";
  const label = loading
    ? "Checking…"
    : error
      ? "API offline"
      : ok
        ? "API healthy"
        : "API ?";
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${tone}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}
